package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.*
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

/**
 * Compact team legend row for the Pro event NEW layout — a horizontal list of
 * coloured team badges followed by a "Create Team" CTA aligned to the right.
 * Figma node 7902:323625 — each team is a 38px mini player-card-shaped chip
 * (textured colour plate + glow + 3-person silhouette) followed by the team
 * name in `Text/Secondary`.
 */
@Composable
fun TeamColorPills(
    teams: List<EventTeamDto>,
    isAdmin: Boolean,
    onCreateTeam: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth().sHeight(48),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(modifier = Modifier.sGap(24), verticalAlignment = Alignment.CenterVertically) {
            teams.forEach { team -> TeamPill(team = team) }
        }
        if (isAdmin) CreateTeamCTA(onClick = onCreateTeam)
    }
}

@Composable
private fun TeamPill(team: EventTeamDto) {
    // Figma 7902:323625 — 38px circle, 8px gap, 16px Urbanist-Medium label.
    Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
        TeamColorBadge(color = team.color ?: "#0D184A", size = 38)
        SpanText(
            team.name.uppercase(),
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(FontWeight.Medium)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("line-height", s(18))
                    property("letter-spacing", s(0.032))
                    property("white-space", "nowrap")
                },
        )
    }
}

/**
 * Circular team-card swatch — same texture+glow+silhouette composition as
 * the colour picker in `CreateTeamPopup`, shrunk so it reads as a chip glyph.
 * Texture and decoration are masked into the circle via overflow-clip. The
 * inner glow/deco/silhouette positions scale with `size` so a 26px badge
 * stays visually balanced at the smaller scale.
 */
@Composable
internal fun TeamColorBadge(color: String, size: Int = 38) {
    val glow = (size * 72) / 38
    val glowLeft = -(size * 17) / 38
    val glowTop = -(size * 30) / 38
    val deco = (size * 110) / 38
    val decoTop = -(size * 42) / 38
    val decoShift = -(size * 7) / 38
    val silWidth = (size * 19) / 38
    val silHeight = (size * 13) / 38
    Box(
        modifier = Modifier
            .sSize(size)
            .sBorderRadius(100)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .styleModifier {
                property("background-color", color)
                property("border", "0.5px solid rgba(13, 24, 74, 0.6)")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Card texture — gives the flat colour a printed-card feel.
        Image(
            src = "/images/team-card-texture.png",
            modifier = Modifier
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .fillMaxSize()
                .styleModifier {
                    property("opacity", "0.55")
                    property("object-fit", "cover")
                    property("pointer-events", "none")
                }
        )
        // Soft light glow biased to the top-left of the swatch.
        Image(
            src = "/images/team-card-glow.svg",
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("width", s(glow))
                    property("height", s(glow))
                    property("left", s(glowLeft))
                    property("top", s(glowTop))
                    property("pointer-events", "none")
                }
        )
        // Decorative card stripe on the right edge.
        Image(
            src = "/images/team-card-deco.svg",
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("width", s(deco))
                    property("height", s(deco))
                    property("left", "75%")
                    property("top", s(decoTop))
                    property("transform", "translateX(${s(decoShift)}) scaleY(-1) rotate(180deg)")
                    property("pointer-events", "none")
                }
        )
        // Three-person silhouette — black @ 50% opacity so it reads like a
        // tinted shadow on top of the coloured swatch.
        Icon(
            src = AppIcons.TeamPeople,
            modifier = Modifier
                .sWidth(silWidth).sHeight(silHeight)
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "1")
                    property("filter", "brightness(0) saturate(100%)")
                    property("opacity", "0.5")
                    property("pointer-events", "none")
                }
        )
    }
}

@Composable
private fun CreateTeamCTA(onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.sSize(14).styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                },
            )
        }
        Box(
            modifier = Modifier
                .sWidth(7)
                .sHeight(14)
                .backgroundColor(sportColors.primary)
                .sMarginLeft(-4)
                .zIndex(0),
        )
        Box(
            modifier = Modifier
                .sHeight(36)
                .sBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .sPadding(8, 22)
                .sMarginLeft(-4),
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                t.eventCreateTeam,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(FontWeight.Medium)
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") },
            )
        }
    }
}
